import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Users, 
  DollarSign, 
  TrendingUp, 
  Copy, 
  Check, 
  ExternalLink,
  CreditCard,
  Clock,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Wallet,
  Shield,
  Fingerprint,
  Cookie,
  HardDrive,
  MonitorSmartphone,
  Share2,
  MessageCircle,
  Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { safeLocalStorage } from '../utils/safeStorage';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ReferralProgram() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [stats, setStats] = useState({
    total_referrals: 0,
    monthly_referrals: 0,
    annual_referrals: 0,
    active_referrals: 0,
    pending_earnings: 0,
    available_earnings: 0,
    total_withdrawn: 0,
    referral_code: '',
    referral_link: ''
  });
  const [referrals, setReferrals] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Withdrawal form
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawError, setWithdrawError] = useState('');
  const [withdrawSuccess, setWithdrawSuccess] = useState(null);

  const token = safeLocalStorage.getItem('auth_token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch stats
      const statsRes = await fetch(`${API}/api/referral/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      // Fetch referrals
      const referralsRes = await fetch(`${API}/api/referral/referrals?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (referralsRes.ok) {
        const referralsData = await referralsRes.json();
        setReferrals(referralsData);
      }

      // Fetch withdrawals
      const withdrawalsRes = await fetch(`${API}/api/referral/withdrawals?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (withdrawalsRes.ok) {
        const withdrawalsData = await withdrawalsRes.json();
        setWithdrawals(withdrawalsData);
      }
    } catch (error) {
      console.error('Error fetching referral data:', error);
    }
    setLoading(false);
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Share functions
  const shareOnWhatsApp = () => {
    const text = isRTL 
      ? `🎁 انضم إلى منصة oeia.io واحصل على أفضل أدوات التسويق!\n\n✨ استخدم رابط الإحالة الخاص بي:\n${stats.referral_link}\n\nأو أدخل كود الإحالة: ${stats.referral_code}`
      : `🎁 Join oeia.io and get the best marketing tools!\n\n✨ Use my referral link:\n${stats.referral_link}\n\nOr enter referral code: ${stats.referral_code}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareOnTwitter = () => {
    const text = isRTL
      ? `🚀 انضم إلى منصة @oeia_io للتسويق الاحترافي!\n\n💰 احصل على عمولات تصل إلى $240\n\n🔗 ${stats.referral_link}`
      : `🚀 Join @oeia_io for professional marketing!\n\n💰 Earn commissions up to $240\n\n🔗 ${stats.referral_link}`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareOnTelegram = () => {
    const text = isRTL
      ? `🎁 انضم إلى منصة oeia.io واحصل على أفضل أدوات التسويق!\n\n✨ استخدم رابط الإحالة الخاص بي أو أدخل كود: ${stats.referral_code}`
      : `🎁 Join oeia.io and get the best marketing tools!\n\n✨ Use my referral link or enter code: ${stats.referral_code}`;
    window.open(`https://t.me/share/url?url=${encodeURIComponent(stats.referral_link)}&text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareOnMessenger = () => {
    window.open(`https://www.facebook.com/dialog/send?link=${encodeURIComponent(stats.referral_link)}&app_id=291494419107518&redirect_uri=${encodeURIComponent(window.location.href)}`, '_blank');
  };

  const shareViaEmail = () => {
    const subject = isRTL
      ? `🎁 دعوة للانضمام إلى منصة oeia.io`
      : `🎁 Invitation to join oeia.io platform`;
    const body = isRTL
      ? `مرحباً،\n\nأدعوك للانضمام إلى منصة oeia.io - أفضل منصة للتسويق الاحترافي!\n\n✨ مميزات المنصة:\n• أدوات تسويق متقدمة\n• تحليلات شاملة\n• دعم فني متميز\n\n🔗 انضم الآن عبر رابط الإحالة:\n${stats.referral_link}\n\nأو استخدم كود الإحالة: ${stats.referral_code}\n\nمع تحياتي`
      : `Hello,\n\nI invite you to join oeia.io - the best professional marketing platform!\n\n✨ Platform features:\n• Advanced marketing tools\n• Comprehensive analytics\n• Excellent support\n\n🔗 Join now via my referral link:\n${stats.referral_link}\n\nOr use referral code: ${stats.referral_code}\n\nBest regards`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
  };

  const handleWithdraw = async () => {
    setWithdrawError('');
    setWithdrawSuccess(null);
    
    if (!withdrawAmount || parseFloat(withdrawAmount) < 50) {
      setWithdrawError(isRTL ? 'الحد الأدنى للسحب هو $50 USDT' : 'Minimum withdrawal is $50 USDT');
      return;
    }
    
    if (!walletAddress || !walletAddress.startsWith('T') || walletAddress.length !== 34) {
      setWithdrawError(isRTL ? 'عنوان محفظة TRC20 غير صالح (يجب أن يبدأ بـ T ويتكون من 34 حرفاً)' : 'Invalid TRC20 wallet address (must start with T and be 34 characters)');
      return;
    }

    setWithdrawLoading(true);
    try {
      const res = await fetch(`${API}/api/referral/withdraw`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseFloat(withdrawAmount),
          payment_method: 'usdt_trc20',
          wallet_address: walletAddress
        })
      });

      const data = await res.json();
      
      if (res.ok && data.success) {
        setWithdrawSuccess({
          amount: data.net_amount,
          tx_hash: data.tx_hash,
          message: data.message
        });
        setWithdrawAmount('');
        fetchData();
      } else {
        setWithdrawError(data.detail || (isRTL ? 'حدث خطأ' : 'An error occurred'));
      }
    } catch (error) {
      setWithdrawError(isRTL ? 'حدث خطأ في الاتصال' : 'Connection error');
    }
    setWithdrawLoading(false);
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-500/20 text-green-400 border-green-500/30',
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      expired: 'bg-red-500/20 text-red-400 border-red-500/30',
      cancelled: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      completed: 'bg-green-500/20 text-green-400 border-green-500/30',
      processing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    
    const labels = {
      active: isRTL ? 'نشط' : 'Active',
      pending: isRTL ? 'معلق' : 'Pending',
      expired: isRTL ? 'منتهي' : 'Expired',
      cancelled: isRTL ? 'ملغي' : 'Cancelled',
      completed: isRTL ? 'مكتمل' : 'Completed',
      processing: isRTL ? 'قيد المعالجة' : 'Processing',
      rejected: isRTL ? 'مرفوض' : 'Rejected'
    };

    return (
      <span className={`px-2 py-1 text-xs rounded-full border ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white p-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
            <Users className="w-8 h-8 text-purple-400" />
            {isRTL ? 'برنامج الإحالة' : 'Referral Program'}
          </h1>
          <p className="text-gray-400">
            {isRTL 
              ? 'ادعُ أصدقاءك واحصل على عمولات متكررة!'
              : 'Invite friends and earn recurring commissions!'}
          </p>
        </div>

        {/* Marketing Banner */}
        <div className="mb-8 relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-900/60 via-indigo-900/40 to-blue-900/60 border border-purple-500/30">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOGM5LjkgMCAxNy45MTgtOC4wMDcgMTcuOTk3LTE3LjkzIiBzdHJva2U9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiIHN0cm9rZS13aWR0aD0iMiIvPjwvZz48L3N2Zz4=')] opacity-20"></div>
          <div className="relative p-8 text-center">
            {/* Stars decoration */}
            <div className="absolute top-4 left-8 text-yellow-400 text-2xl">✦</div>
            <div className="absolute top-6 right-12 text-purple-400 text-xl">✦</div>
            <div className="absolute bottom-6 left-16 text-blue-400 text-lg">✦</div>
            
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 px-4 py-2 rounded-full border border-amber-500/30 mb-4">
              <DollarSign className="w-5 h-5 text-amber-400" />
              <span className="text-amber-300 font-semibold text-sm">
                {isRTL ? 'من أعلى معدلات العمولة للمسوقين' : 'Top Commission Rates for Marketers'}
              </span>
            </div>
            
            <h2 className="text-2xl md:text-3xl font-bold mb-4 bg-gradient-to-r from-white via-purple-200 to-blue-200 bg-clip-text text-transparent">
              {isRTL 
                ? 'قم بإحالة الأصدقاء واحصل على عمولة' 
                : 'Refer Friends and Earn Commission'}
            </h2>
            
            <div className="flex flex-wrap justify-center gap-4 mb-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-4 border border-white/10">
                <div className="text-3xl md:text-4xl font-bold text-green-400">$40</div>
                <div className="text-xs text-gray-400 mt-1">{isRTL ? 'الحد الأدنى' : 'Minimum'}</div>
              </div>
              <div className="flex items-center text-2xl text-gray-500">→</div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-4 border border-white/10">
                <div className="text-3xl md:text-4xl font-bold text-amber-400">$240</div>
                <div className="text-xs text-gray-400 mt-1">{isRTL ? 'الحد الأقصى' : 'Maximum'}</div>
              </div>
            </div>
            
            <p className="text-gray-300 max-w-xl mx-auto mb-6 leading-relaxed">
              {isRTL 
                ? 'واستفد من تكرار الحصول عليها ثلاثة أشهر متتالية من دون أي عناء أو تعب!'
                : 'And benefit from receiving it for 3 consecutive months without any hassle!'}
            </p>
            
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-green-500/10 to-emerald-500/10 px-5 py-3 rounded-xl border border-green-500/20">
              <CheckCircle className="w-6 h-6 text-green-400" />
              <div className="text-right">
                <div className="text-green-400 font-bold text-sm">
                  {isRTL ? 'من أقوى منصات العمل الجاد والحقيقي والصادق' : 'One of the Most Reliable Platforms'}
                </div>
                <div className="text-gray-400 text-xs mt-1">
                  {isRTL 
                    ? 'لا تشترك ولا تنصح أحداً بالاشتراك في المنصة إذا لم تحصل على ما وعدناك به'
                    : "Don't subscribe or recommend if we don't deliver on our promises"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-white/10 pb-4">
          {[
            { id: 'overview', label: isRTL ? 'نظرة عامة' : 'Overview' },
            { id: 'referrals', label: isRTL ? 'الإحالات' : 'Referrals' },
            { id: 'withdrawals', label: isRTL ? 'السحوبات' : 'Withdrawals' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg transition-all ${
                activeTab === tab.id
                  ? 'bg-purple-600 text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {/* Total Referrals */}
              <div className="bg-gradient-to-br from-purple-900/40 to-purple-800/20 rounded-xl p-6 border border-purple-500/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-purple-500/20 rounded-lg">
                    <Users className="w-6 h-6 text-purple-400" />
                  </div>
                  <span className="text-gray-400 text-sm">
                    {isRTL ? 'إجمالي الإحالات' : 'Total Referrals'}
                  </span>
                </div>
                <div className="text-3xl font-bold">{stats.total_referrals}</div>
                <div className="text-xs text-gray-500 mt-2">
                  {isRTL ? `${stats.monthly_referrals} شهري • ${stats.annual_referrals} سنوي` 
                        : `${stats.monthly_referrals} monthly • ${stats.annual_referrals} annual`}
                </div>
              </div>

              {/* Active Referrals */}
              <div className="bg-gradient-to-br from-green-900/40 to-green-800/20 rounded-xl p-6 border border-green-500/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-green-500/20 rounded-lg">
                    <RefreshCw className="w-6 h-6 text-green-400" />
                  </div>
                  <span className="text-gray-400 text-sm">
                    {isRTL ? 'إحالات نشطة' : 'Active Referrals'}
                  </span>
                </div>
                <div className="text-3xl font-bold text-green-400">{stats.active_referrals}</div>
                <div className="text-xs text-gray-500 mt-2">
                  {isRTL ? 'الدفع التلقائي مفعّل' : 'Auto-renewal enabled'}
                </div>
              </div>

              {/* Available Earnings */}
              <div className="bg-gradient-to-br from-blue-900/40 to-blue-800/20 rounded-xl p-6 border border-blue-500/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-blue-500/20 rounded-lg">
                    <Wallet className="w-6 h-6 text-blue-400" />
                  </div>
                  <span className="text-gray-400 text-sm">
                    {isRTL ? 'الرصيد المتاح' : 'Available Balance'}
                  </span>
                </div>
                <div className="text-3xl font-bold text-blue-400">${stats.available_earnings.toFixed(2)}</div>
                <div className="text-xs text-gray-500 mt-2">
                  {isRTL ? `معلق: $${stats.pending_earnings.toFixed(2)}` 
                        : `Pending: $${stats.pending_earnings.toFixed(2)}`}
                </div>
              </div>

              {/* Total Withdrawn */}
              <div className="bg-gradient-to-br from-amber-900/40 to-amber-800/20 rounded-xl p-6 border border-amber-500/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-amber-500/20 rounded-lg">
                    <DollarSign className="w-6 h-6 text-amber-400" />
                  </div>
                  <span className="text-gray-400 text-sm">
                    {isRTL ? 'إجمالي المسحوب' : 'Total Withdrawn'}
                  </span>
                </div>
                <div className="text-3xl font-bold text-amber-400">${stats.total_withdrawn.toFixed(2)}</div>
              </div>
            </div>

            {/* Referral Link & Code */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Referral Link */}
              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <ExternalLink className="w-5 h-5 text-purple-400" />
                  {isRTL ? 'رابط الإحالة' : 'Referral Link'}
                </h3>
                <div className="flex gap-2">
                  <Input
                    value={stats.referral_link}
                    readOnly
                    className="bg-white/5 border-white/10 text-gray-300"
                  />
                  <Button
                    onClick={() => copyToClipboard(stats.referral_link, 'link')}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {copied === 'link' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {isRTL ? 'شارك هذا الرابط مع أصدقائك' : 'Share this link with your friends'}
                </p>
              </div>

              {/* Referral Code */}
              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-blue-400" />
                  {isRTL ? 'كود الإحالة' : 'Referral Code'}
                </h3>
                <div className="flex gap-2">
                  <div className="flex-1 bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-lg p-4 text-center border border-purple-500/30">
                    <span className="text-2xl font-mono font-bold tracking-widest text-purple-300">
                      {stats.referral_code}
                    </span>
                  </div>
                  <Button
                    onClick={() => copyToClipboard(stats.referral_code, 'code')}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {copied === 'code' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {isRTL ? 'يمكن لأصدقائك إدخال هذا الكود عند التسجيل' : 'Friends can enter this code during registration'}
                </p>
              </div>
            </div>

            {/* Quick Share Buttons */}
            <div className="bg-gradient-to-r from-emerald-900/30 via-teal-900/30 to-cyan-900/30 rounded-xl p-6 border border-emerald-500/20 mb-8">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Share2 className="w-5 h-5 text-emerald-400" />
                {isRTL ? 'شارك رابطك بسرعة' : 'Quick Share Your Link'}
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                {isRTL 
                  ? 'انقر على أي زر لمشاركة رابط الإحالة الخاص بك مباشرة'
                  : 'Click any button to share your referral link instantly'}
              </p>
              <div className="flex flex-wrap gap-3">
                {/* WhatsApp */}
                <button
                  onClick={shareOnWhatsApp}
                  className="flex items-center gap-2 px-5 py-3 bg-[#25D366]/20 hover:bg-[#25D366]/30 border border-[#25D366]/40 rounded-xl transition-all hover:scale-105"
                >
                  <svg className="w-5 h-5" fill="#25D366" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span className="text-[#25D366] font-medium">WhatsApp</span>
                </button>

                {/* Twitter/X */}
                <button
                  onClick={shareOnTwitter}
                  className="flex items-center gap-2 px-5 py-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl transition-all hover:scale-105"
                >
                  <svg className="w-5 h-5" fill="white" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  <span className="text-white font-medium">X (Twitter)</span>
                </button>

                {/* Telegram */}
                <button
                  onClick={shareOnTelegram}
                  className="flex items-center gap-2 px-5 py-3 bg-[#0088cc]/20 hover:bg-[#0088cc]/30 border border-[#0088cc]/40 rounded-xl transition-all hover:scale-105"
                >
                  <svg className="w-5 h-5" fill="#0088cc" viewBox="0 0 24 24">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                  </svg>
                  <span className="text-[#0088cc] font-medium">Telegram</span>
                </button>

                {/* Copy Link */}
                <button
                  onClick={() => copyToClipboard(stats.referral_link, 'share')}
                  className="flex items-center gap-2 px-5 py-3 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/40 rounded-xl transition-all hover:scale-105"
                >
                  {copied === 'share' ? (
                    <>
                      <Check className="w-5 h-5 text-green-400" />
                      <span className="text-green-400 font-medium">{isRTL ? 'تم النسخ!' : 'Copied!'}</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-5 h-5 text-purple-400" />
                      <span className="text-purple-400 font-medium">{isRTL ? 'نسخ الرابط' : 'Copy Link'}</span>
                    </>
                  )}
                </button>

                {/* Facebook Messenger */}
                <button
                  onClick={shareOnMessenger}
                  className="flex items-center gap-2 px-5 py-3 bg-[#0084FF]/20 hover:bg-[#0084FF]/30 border border-[#0084FF]/40 rounded-xl transition-all hover:scale-105"
                >
                  <svg className="w-5 h-5" fill="#0084FF" viewBox="0 0 24 24">
                    <path d="M12 0C5.373 0 0 4.974 0 11.111c0 3.498 1.744 6.614 4.469 8.654V24l4.088-2.242c1.092.3 2.246.464 3.443.464 6.627 0 12-4.974 12-11.111S18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8l3.131 3.259L19.752 8l-6.561 6.963z"/>
                  </svg>
                  <span className="text-[#0084FF] font-medium">Messenger</span>
                </button>

                {/* Email */}
                <button
                  onClick={shareViaEmail}
                  className="flex items-center gap-2 px-5 py-3 bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/40 rounded-xl transition-all hover:scale-105"
                >
                  <Mail className="w-5 h-5 text-rose-400" />
                  <span className="text-rose-400 font-medium">{isRTL ? 'بريد إلكتروني' : 'Email'}</span>
                </button>
              </div>
            </div>

            {/* Advanced Tracking System Info */}
            <div className="bg-gradient-to-br from-slate-900/60 via-zinc-900/40 to-neutral-900/60 rounded-2xl border border-white/10 mb-8 overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 px-6 py-4 border-b border-white/10">
                <h3 className="text-xl font-bold flex items-center gap-3">
                  <Shield className="w-6 h-6 text-blue-400" />
                  {isRTL ? 'نظام تتبع متطور يحمي عمولاتك' : 'Advanced Tracking Protects Your Commissions'}
                </h3>
                <p className="text-gray-400 text-sm mt-1">
                  {isRTL 
                    ? 'تقنيات متعددة تضمن حصولك على عمولتك حتى لو أغلق العميل المتصفح!'
                    : 'Multiple technologies ensure you get your commission even if the customer closes the browser!'}
                </p>
              </div>

              <div className="p-6">
                {/* Protection Layers */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-4 flex items-center gap-2 text-emerald-400">
                    <CheckCircle className="w-5 h-5" />
                    {isRTL ? 'طبقات الحماية المتعددة' : 'Multiple Protection Layers'}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Cookie */}
                    <div className="flex items-start gap-3 bg-white/5 rounded-xl p-4 border border-white/5 hover:border-amber-500/30 transition-all">
                      <div className="p-2 bg-amber-500/20 rounded-lg shrink-0">
                        <Cookie className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <div className="font-medium text-amber-300">
                          {isRTL ? 'كوكيز ذكية' : 'Smart Cookies'}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {isRTL 
                            ? 'تبقى محفوظة 30 يوم حتى لو أغلق المتصفح'
                            : 'Saved for 30 days even after browser closes'}
                        </div>
                        <div className="text-xs text-amber-500/80 mt-1">
                          {isRTL ? '⏱️ 30 يوم' : '⏱️ 30 days'}
                        </div>
                      </div>
                    </div>

                    {/* LocalStorage */}
                    <div className="flex items-start gap-3 bg-white/5 rounded-xl p-4 border border-white/5 hover:border-blue-500/30 transition-all">
                      <div className="p-2 bg-blue-500/20 rounded-lg shrink-0">
                        <HardDrive className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <div className="font-medium text-blue-300">
                          {isRTL ? 'تخزين محلي احتياطي' : 'Local Storage Backup'}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {isRTL 
                            ? 'نسخة احتياطية آمنة على جهاز العميل'
                            : 'Secure backup copy on customer device'}
                        </div>
                        <div className="text-xs text-blue-500/80 mt-1">
                          {isRTL ? '💾 دائم' : '💾 Permanent'}
                        </div>
                      </div>
                    </div>

                    {/* Session Storage */}
                    <div className="flex items-start gap-3 bg-white/5 rounded-xl p-4 border border-white/5 hover:border-purple-500/30 transition-all">
                      <div className="p-2 bg-purple-500/20 rounded-lg shrink-0">
                        <MonitorSmartphone className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <div className="font-medium text-purple-300">
                          {isRTL ? 'تتبع الجلسة' : 'Session Tracking'}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {isRTL 
                            ? 'يتتبع نشاط العميل خلال الجلسة الحالية'
                            : 'Tracks customer activity during current session'}
                        </div>
                        <div className="text-xs text-purple-500/80 mt-1">
                          {isRTL ? '🔄 فوري' : '🔄 Real-time'}
                        </div>
                      </div>
                    </div>

                    {/* Device Fingerprint */}
                    <div className="flex items-start gap-3 bg-white/5 rounded-xl p-4 border border-white/5 hover:border-green-500/30 transition-all">
                      <div className="p-2 bg-green-500/20 rounded-lg shrink-0">
                        <Fingerprint className="w-5 h-5 text-green-400" />
                      </div>
                      <div>
                        <div className="font-medium text-green-300">
                          {isRTL ? 'بصمة الجهاز' : 'Device Fingerprint'}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {isRTL 
                            ? 'يتعرف على الجهاز حتى بدون كوكيز'
                            : 'Identifies device even without cookies'}
                        </div>
                        <div className="text-xs text-green-500/80 mt-1">
                          {isRTL ? '🔒 مستمر' : '🔒 Persistent'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Protected Scenarios */}
                <div>
                  <h4 className="text-lg font-semibold mb-4 flex items-center gap-2 text-cyan-400">
                    <Shield className="w-5 h-5" />
                    {isRTL ? 'السيناريوهات المحمية' : 'Protected Scenarios'}
                  </h4>
                  <div className="space-y-2">
                    {/* Scenario 1 */}
                    <div className="flex items-center gap-3 bg-green-500/10 rounded-lg px-4 py-3 border border-green-500/20">
                      <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
                      <span className="text-sm text-gray-300">
                        {isRTL 
                          ? 'العميل زار رابطك ثم أغلق المتصفح وعاد لاحقاً؟ ← عمولتك محفوظة!'
                          : 'Customer visited your link, closed browser, returned later? → Your commission is saved!'}
                      </span>
                    </div>

                    {/* Scenario 2 */}
                    <div className="flex items-center gap-3 bg-green-500/10 rounded-lg px-4 py-3 border border-green-500/20">
                      <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
                      <span className="text-sm text-gray-300">
                        {isRTL 
                          ? 'العميل ذهب مباشرة لصفحة التسجيل؟ ← كود الإحالة يُملأ تلقائياً!'
                          : 'Customer went directly to registration? → Referral code auto-fills!'}
                      </span>
                    </div>

                    {/* Scenario 3 */}
                    <div className="flex items-center gap-3 bg-green-500/10 rounded-lg px-4 py-3 border border-green-500/20">
                      <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
                      <span className="text-sm text-gray-300">
                        {isRTL 
                          ? 'العميل مسح بيانات المتصفح؟ ← الكوكيز والبصمة تحميك!'
                          : 'Customer cleared browser data? → Cookies & fingerprint protect you!'}
                      </span>
                    </div>

                    {/* Scenario 4 */}
                    <div className="flex items-center gap-3 bg-amber-500/10 rounded-lg px-4 py-3 border border-amber-500/20">
                      <AlertCircle className="w-5 h-5 text-amber-400 shrink-0" />
                      <span className="text-sm text-gray-300">
                        {isRTL 
                          ? 'محاولات تسجيل متعددة من نفس الجهاز؟ ← يتم تسجيلها ومراقبتها!'
                          : 'Multiple registration attempts from same device? → Logged & monitored!'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Bottom Note */}
                <div className="mt-6 p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/20">
                  <p className="text-sm text-gray-300 text-center flex items-center justify-center gap-2">
                    <Shield className="w-4 h-4 text-blue-400" />
                    {isRTL 
                      ? '🔐 نظام التتبع يعمل تلقائياً - لا تحتاج لفعل أي شيء سوى مشاركة رابطك!'
                      : '🔐 Tracking system works automatically - just share your link!'}
                  </p>
                </div>
              </div>
            </div>

            {/* Commission Info */}
            <div className="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-xl p-6 border border-indigo-500/20 mb-8">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-indigo-400" />
                {isRTL ? 'نسب العمولات' : 'Commission Rates'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white/5 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">{isRTL ? 'الاشتراك الشهري' : 'Monthly Subscription'}</span>
                    <span className="text-2xl font-bold text-green-400">15%</span>
                  </div>
                </div>
                <div className="bg-white/5 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">{isRTL ? 'الاشتراك السنوي' : 'Annual Subscription'}</span>
                    <span className="text-2xl font-bold text-green-400">20%</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                <p className="text-sm text-amber-300 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  {isRTL 
                    ? 'العمولة متكررة لـ 3 دورات اشتراك متتالية بشرط تفعيل الدفع التلقائي'
                    : 'Commission is recurring for 3 consecutive subscription cycles if auto-renewal is enabled'}
                </p>
              </div>
            </div>

            {/* Withdraw Button */}
            <div className="flex justify-center">
              <Button
                onClick={() => setShowWithdrawModal(true)}
                disabled={stats.available_earnings < 50}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 px-8 py-3 text-lg"
              >
                <DollarSign className="w-5 h-5 mr-2" />
                {isRTL ? 'طلب سحب' : 'Request Withdrawal'}
              </Button>
            </div>
            {stats.available_earnings < 50 && (
              <p className="text-center text-gray-500 text-sm mt-2">
                {isRTL 
                  ? `الحد الأدنى للسحب $50 (المتاح: $${stats.available_earnings.toFixed(2)})`
                  : `Minimum withdrawal $50 (Available: $${stats.available_earnings.toFixed(2)})`}
              </p>
            )}
          </>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">
                      {isRTL ? 'المستخدم' : 'User'}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">
                      {isRTL ? 'النوع' : 'Type'}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">
                      {isRTL ? 'العمولة' : 'Commission'}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">
                      {isRTL ? 'التجديدات' : 'Renewals'}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">
                      {isRTL ? 'الدفع التلقائي' : 'Auto-Renewal'}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">
                      {isRTL ? 'الحالة' : 'Status'}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">
                      {isRTL ? 'التاريخ' : 'Date'}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {referrals.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                        {isRTL ? 'لا توجد إحالات بعد' : 'No referrals yet'}
                      </td>
                    </tr>
                  ) : (
                    referrals.map((ref) => (
                      <tr key={ref.id} className="hover:bg-white/5">
                        <td className="px-4 py-3">
                          <div>
                            <div className="font-medium">{ref.referred_user_name || 'N/A'}</div>
                            <div className="text-xs text-gray-500">{ref.referred_user_email || 'N/A'}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            ref.subscription_type === 'annual' || ref.subscription_type === 'yearly'
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-purple-500/20 text-purple-400'
                          }`}>
                            {ref.subscription_type === 'annual' || ref.subscription_type === 'yearly'
                              ? (isRTL ? 'سنوي' : 'Annual')
                              : (isRTL ? 'شهري' : 'Monthly')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-green-400 font-medium">
                            {ref.commission_rate || 15}%
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-gray-300">
                            {ref.renewal_count || 0} / 3
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {ref.auto_renewal_enabled ? (
                            <CheckCircle className="w-5 h-5 text-green-400" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-400" />
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(ref.status)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {new Date(ref.created_at).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US')}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Withdrawals Tab */}
        {activeTab === 'withdrawals' && (
          <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">
                      {isRTL ? 'المبلغ' : 'Amount'}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">
                      {isRTL ? 'الرسوم' : 'Fees'}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">
                      {isRTL ? 'الصافي' : 'Net'}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">
                      {isRTL ? 'طريقة الدفع' : 'Payment Method'}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">
                      {isRTL ? 'الحالة' : 'Status'}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">
                      {isRTL ? 'التاريخ' : 'Date'}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {withdrawals.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                        {isRTL ? 'لا توجد سحوبات بعد' : 'No withdrawals yet'}
                      </td>
                    </tr>
                  ) : (
                    withdrawals.map((w) => (
                      <tr key={w.id} className="hover:bg-white/5">
                        <td className="px-4 py-3 font-medium">
                          ${w.amount?.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-red-400">
                          -${w.fees?.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-green-400 font-medium">
                          ${w.net_amount?.toFixed(2)}
                        </td>
                        <td className="px-4 py-3">
                          <span className="capitalize">{w.payment_method}</span>
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(w.status)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {new Date(w.created_at).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US')}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Withdrawal Modal */}
        {showWithdrawModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1a1a2e] rounded-xl p-6 w-full max-w-md border border-white/10">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <DollarSign className="w-6 h-6 text-green-400" />
                {isRTL ? 'طلب سحب' : 'Request Withdrawal'}
              </h3>

              <div className="space-y-4">
                {/* Available Balance */}
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                  <div className="text-sm text-gray-400">
                    {isRTL ? 'الرصيد المتاح' : 'Available Balance'}
                  </div>
                  <div className="text-2xl font-bold text-green-400">
                    ${stats.available_earnings.toFixed(2)}
                  </div>
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">
                    {isRTL ? 'المبلغ (USDT)' : 'Amount (USDT)'}
                  </label>
                  <Input
                    type="number"
                    min="50"
                    max={stats.available_earnings}
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    placeholder={isRTL ? 'الحد الأدنى $50 USDT' : 'Minimum $50 USDT'}
                    className="bg-white/5 border-white/10"
                  />
                </div>

                {/* USDT TRC20 Wallet Address */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">
                    {isRTL ? 'عنوان محفظة USDT (TRC20)' : 'USDT Wallet Address (TRC20)'}
                  </label>
                  <Input
                    type="text"
                    value={walletAddress}
                    onChange={(e) => setWalletAddress(e.target.value)}
                    placeholder="TXxx...xxxxx"
                    className="bg-white/5 border-white/10 font-mono"
                    dir="ltr"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {isRTL 
                      ? '⚠️ تأكد من أن العنوان على شبكة TRON (TRC20) فقط!'
                      : '⚠️ Make sure the address is on TRON network (TRC20) only!'}
                  </p>
                </div>

                {/* Instant Payout Info */}
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-sm">
                  <div className="flex items-center gap-2 text-green-400 font-semibold mb-2">
                    <CheckCircle className="w-4 h-4" />
                    {isRTL ? 'سحب فوري تلقائي!' : 'Instant Automatic Payout!'}
                  </div>
                  <p className="text-gray-400 text-xs">
                    {isRTL 
                      ? 'سيتم إرسال USDT إلى محفظتك فوراً بدون أي تأخير.'
                      : 'USDT will be sent to your wallet instantly with no delay.'}
                  </p>
                </div>

                {/* Fees Info */}
                {withdrawAmount && parseFloat(withdrawAmount) >= 50 && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-sm">
                    <div className="flex justify-between text-gray-400">
                      <span>{isRTL ? 'المبلغ' : 'Amount'}:</span>
                      <span>${parseFloat(withdrawAmount).toFixed(2)} USDT</span>
                    </div>
                    <div className="flex justify-between text-gray-400">
                      <span>{isRTL ? 'رسوم الشبكة' : 'Network Fee'}:</span>
                      <span className="text-red-400">-$1.00 USDT</span>
                    </div>
                    <div className="flex justify-between font-bold text-green-400 mt-1 pt-1 border-t border-white/10">
                      <span>{isRTL ? 'ستستلم' : 'You will receive'}:</span>
                      <span>${(parseFloat(withdrawAmount) - 1).toFixed(2)} USDT</span>
                    </div>
                  </div>
                )}

                {/* Success Message */}
                {withdrawSuccess && (
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 text-center">
                    <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-2" />
                    <h4 className="text-green-400 font-bold mb-1">
                      {isRTL ? 'تم السحب بنجاح!' : 'Withdrawal Successful!'}
                    </h4>
                    <p className="text-gray-400 text-sm mb-2">
                      {isRTL 
                        ? `تم إرسال $${withdrawSuccess.amount} USDT إلى محفظتك`
                        : `$${withdrawSuccess.amount} USDT sent to your wallet`}
                    </p>
                    {withdrawSuccess.tx_hash && (
                      <a 
                        href={`https://tronscan.org/#/transaction/${withdrawSuccess.tx_hash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cyan-400 hover:underline text-sm flex items-center justify-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        {isRTL ? 'عرض المعاملة' : 'View Transaction'}
                      </a>
                    )}
                  </div>
                )}

                {/* Error */}
                {withdrawError && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm">
                    {withdrawError}
                  </div>
                )}

                {/* Actions */}
                {!withdrawSuccess && (
                  <div className="flex gap-2 pt-2">
                    <Button
                      onClick={() => {
                        setShowWithdrawModal(false);
                        setWithdrawError('');
                        setWithdrawSuccess(null);
                      }}
                      variant="outline"
                      className="flex-1 border-white/10"
                    >
                      {isRTL ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button
                      onClick={handleWithdraw}
                      disabled={withdrawLoading}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      {withdrawLoading ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        isRTL ? 'سحب فوري' : 'Withdraw Now'
                      )}
                    </Button>
                  </div>
                )}

                {withdrawSuccess && (
                  <Button
                    onClick={() => {
                      setShowWithdrawModal(false);
                      setWithdrawError('');
                      setWithdrawSuccess(null);
                    }}
                    className="w-full bg-gray-600 hover:bg-gray-700"
                  >
                    {isRTL ? 'إغلاق' : 'Close'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
